import React from 'react';
import { useIntl } from 'react-intl';

import { CategoryRestrictions } from '@ocx/graphql';
import { NoDataView, ScreenLoaderBackdrop } from '@ocx/ui';
import { useMembership, useMembershipProductInterests } from '@ocx/data-membership';
import { ProductInterestsLayout } from './ProductInterestsLayout';
import { ProductCategory } from '../../modules/loyalty/loyalty.types';
import { useProductCategories } from '../../modules/loyalty/useProductCategories';

import { getIsVerified, getPendingVerification } from './product-interests.utils';

import { messages } from './product-interests.messages';

export type ProductInterestsGuardProps = {
  Component: React.ComponentType<{
    isMembershipLoading: boolean;
    membershipBirthdate: string | null;
    initialProductCategories: ProductCategory[];
    productCategories: ProductCategory[];
    pendingVerification: CategoryRestrictions | null;
    isVerified: boolean;
    membershipVerification: CategoryRestrictions | null;
  }>;
};

export const ProductInterestsGuard: React.FC<ProductInterestsGuardProps> = ({ Component }) => {
  const intl = useIntl();
  const { membership, loading: isMembershipLoading } = useMembership();
  const { productCategories: membershipProductCategories, loading: isMembershipProductInterestsLoading } =
    useMembershipProductInterests();
  const { productCategories, loading: isProductCategoriesLoading } = useProductCategories();

  if (!productCategories.length && isProductCategoriesLoading) {
    return (
      <ProductInterestsLayout>
        <ScreenLoaderBackdrop open />
      </ProductInterestsLayout>
    );
  }

  if (!membershipProductCategories.length && isMembershipProductInterestsLoading) {
    return (
      <ProductInterestsLayout>
        <ScreenLoaderBackdrop open />
      </ProductInterestsLayout>
    );
  }

  if (!membership || !productCategories.length) {
    return (
      <ProductInterestsLayout>
        <NoDataView title={intl.formatMessage(messages['product-interests:no-product-interests-title'])} />
      </ProductInterestsLayout>
    );
  }

  const pendingVerification = getPendingVerification({
    pendingVerification: membership.fuelAndConvenienceProfile.pendingVerification,
    membershipProductInterests: membershipProductCategories,
  });

  const isVerified = getIsVerified({
    pendingVerification,
    membershipRestrictionVerification: membership.fuelAndConvenienceProfile.restrictionVerification,
  });

  return (
    <Component
      pendingVerification={pendingVerification}
      isVerified={isVerified}
      initialProductCategories={membershipProductCategories}
      productCategories={productCategories}
      isMembershipLoading={isMembershipLoading}
      membershipBirthdate={membership.birthdate}
      membershipVerification={membership.fuelAndConvenienceProfile.restrictionVerification ?? null}
    />
  );
};
